/**
 * <Announcer/> — the app's ARIA live region. Every outcome (a load finishing, a copy, a processor's
 * OutcomeMessage) is announced through useAnnounce(), so screen-reader users hear what sighted users see.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

type Politeness = 'polite' | 'assertive';
const Ctx = createContext<((message: string, politeness?: Politeness) => void) | null>(null);

export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');
  const timer = useRef<number | undefined>(undefined);

  const announce = useCallback((message: string, politeness: Politeness = 'polite') => {
    const set = politeness === 'assertive' ? setAssertive : setPolite;
    // Clear, then set on the next frame, so the same message twice is still announced.
    set('');
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => set(message), 60);
  }, []);

  // Nothing is said after the region is gone (a message still waiting its frame is dropped with it).
  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <Ctx.Provider value={announce}>
      {children}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true" data-testid="announcer">
        {polite}
      </div>
      <div className="sr-only" role="alert" aria-live="assertive" aria-atomic="true">
        {assertive}
      </div>
    </Ctx.Provider>
  );
}

export function useAnnounce(): (message: string, politeness?: Politeness) => void {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAnnounce outside AnnouncerProvider');
  return v;
}

/** Like useAnnounce, but quiet (does nothing) where there is no live region — a bare component in a unit test. */
export function useAnnounceSafe(): (message: string, politeness?: Politeness) => void {
  const v = useContext(Ctx);
  return v ?? noop;
}
const noop = () => undefined;
